import { DEPLOYMENT_STATUS } from '../constants/enums';
import { dateKeyForStatus } from '../shared/DeploymentLifecycleFields';

const hasContent = (obj) => {
  if (!obj || typeof obj !== 'object') return false;
  return Object.entries(obj).some(([, v]) => {
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'boolean') return v;
    return v !== null && v !== undefined && String(v).trim() !== '';
  });
};

function toApiDateTime(value) {
  if (!value) return undefined;
  const raw = String(value).trim();
  if (!raw) return undefined;
  return raw.length === 16 ? `${raw}:00` : raw;
}

export function buildDeploymentStatuses(identification) {
  const dates = identification?.deploymentStatusDates ?? {};
  const approvalsByStage = identification?.deploymentStatusApprovals ?? {};
  return DEPLOYMENT_STATUS.map((status) => {
    const key = dateKeyForStatus(status);
    const stageDate = dates[key] || undefined;
    const approvals = (approvalsByStage[key] ?? [])
      .filter((row) => ((row.name || '').trim() || (row.email || '').trim()) && row.approvedAt)
      .map((row) => ({
        ...(row.id != null ? { id: row.id } : {}),
        name: (row.name || '').trim() || (row.email || '').trim(),
        email: (row.email || '').trim(),
        approvedAt: toApiDateTime(row.approvedAt),
        ...(row.decision ? { decision: row.decision } : {}),
        ...(row.comment?.trim() ? { comment: row.comment.trim() } : {}),
      }));
    if (!stageDate && approvals.length === 0 && identification?.deploymentStatus !== status) {
      return null;
    }
    return {
      status,
      stageDate: stageDate || undefined,
      current: identification?.deploymentStatus === status,
      approvals,
    };
  }).filter(Boolean);
}

export function buildDataSources(dataModel, meta) {
  const lines = (dataModel?.dataSourcesUsed ?? '')
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
  if (!lines.length) return [];
  const existingIds = meta?.dataSourceIds ?? [];
  return lines.map((sourceName, index) => ({
    ...(existingIds[index] != null ? { id: existingIds[index] } : {}),
    sourceName,
    description: undefined,
  }));
}

function buildDocuments(compliance, meta) {
  const docs = (compliance?.documents ?? []).filter(
    (d) => (d.documentName ?? '').trim() || (d.fileName ?? '').trim() || (d.location ?? '').trim()
  );
  if (!docs.length) return [];
  return docs.map((doc, index) => ({
    ...(doc.id != null ? { id: doc.id } : meta?.documentIds?.[index] != null ? { id: meta.documentIds[index] } : {}),
    documentName: doc.documentName?.trim() || undefined,
    fileName: doc.fileName?.trim() || undefined,
    contentType: doc.contentType || undefined,
    documentType: doc.documentType || undefined,
    deploymentStatus: doc.deploymentStatus || undefined,
    location: doc.location || undefined,
  }));
}

function buildReview(wizardData) {
  const { monitoringReview: monitoring, approvalSignOff: approval, meta } = wizardData;
  const entries = monitoring?.entries ?? [];
  const nextDates = entries.map((e) => e.nextReviewDate).filter(Boolean).sort();
  const reviewData = {
    lastReviewDate: undefined,
    nextReviewDate: nextDates[0] || undefined,
    performanceMonitoring: entries.some((e) => e.monitoringType === 'PERFORMANCE_MONITORING'),
    driftMonitoring: entries.some((e) => e.monitoringType === 'DRIFT_MONITORING'),
    reviewedBy: approval?.reviewedBy?.trim() || undefined,
    reviewerRoleCommittee: approval?.reviewerRoleCommittee?.trim() || undefined,
  };
  if (!hasContent(reviewData)) return [];
  return [{ ...(meta?.reviewId != null ? { id: meta.reviewId } : {}), ...reviewData }];
}

export function buildMonitoringReviews(wizardData) {
  const entries = wizardData.monitoringReview?.entries ?? [];
  return entries
    .filter(
      (e) =>
        e.monitoringReviewId != null
        || (e.monitoringType && (e.reviewAreaControl || '').trim())
    )
    .map((e) => {
      const masterId = e.monitoringReviewId ?? null;
      const joinId =
        e.linkId != null
          ? e.linkId
          : e.id != null && masterId != null && String(e.id) !== String(masterId)
            ? e.id
            : null;
      const approvals = (e.approvals ?? [])
        .filter((a) => a && (a.approverName || a.signatureConfirmation || a.approverRole))
        .map((a) => ({
          ...(a.id != null ? { id: a.id } : {}),
          ...(masterId != null ? { monitoringReviewId: masterId } : {}),
          approverName: a.approverName?.trim() || undefined,
          approverRole: a.approverRole?.trim() || undefined,
          approverCommittee: a.approverCommittee?.trim() || undefined,
          approvalDate: a.approvalDate || undefined,
          approvalStatus: a.approvalStatus || undefined,
          approvalComments: a.approvalComments?.trim() || undefined,
          approvalLevel: a.approvalLevel || undefined,
          signatureConfirmation: a.signatureConfirmation?.trim() || undefined,
          jobPosition: a.jobPosition?.trim() || undefined,
        }));
      return {
        ...(joinId != null ? { id: joinId } : {}),
        ...(masterId != null ? { monitoringReviewId: masterId } : {}),
        monitoringType: e.monitoringType,
        reviewAreaControl: e.reviewAreaControl?.trim() || undefined,
        standardControlReference: e.standardControlReference?.trim() || undefined,
        reviewFrequency: e.reviewFrequency || undefined,
        observationFindings: e.observationFindings?.trim() || undefined,
        riskRating: e.riskRating || undefined,
        correctiveActionRequired: e.correctiveActionRequired?.trim() || undefined,
        actionOwnerEmail: e.actionOwnerEmail?.trim() || undefined,
        status: e.status || undefined,
        nextReviewDate: e.nextReviewDate || undefined,
        evidenceFileName: e.evidenceFileName || undefined,
        evidenceContentType: e.evidenceContentType || undefined,
        evidenceLocation: e.evidenceLocation || undefined,
        reviewer: e.reviewer?.trim() || undefined,
        // Omit empty approvals so backend keeps existing approval rows on link update
        ...(approvals.length ? { approvals } : {}),
      };
    });
}

export function buildIncidents(wizardData) {
  const entries = wizardData.incidentEscalation?.entries ?? [];
  return entries
    .filter((e) => (e.incidentDetails || '').trim())
    .map((e) => ({
      ...(e.id != null ? { id: e.id } : {}),
      ...(e.incidentTicket ? { incidentTicket: e.incidentTicket } : {}),
      incidentDate: e.incidentDate || undefined,
      incidentDetails: e.incidentDetails?.trim() || undefined,
      actionTaken: e.actionTaken?.trim() || undefined,
      rootCause: e.rootCause?.trim() || undefined,
      reportedBy: e.reportedBy?.trim() || undefined,
      incidentReportingStaff: e.incidentReportingStaff?.trim() || undefined,
      riskRegisterIds: Array.isArray(e.riskRegisterIds)
        ? e.riskRegisterIds.filter((id) => id != null)
        : e.riskRegisterId != null
          ? [e.riskRegisterId]
          : [],
      severityLevel: e.severityLevel || e.riskPriority || undefined,
      resolutionStatus: e.resolutionStatus || undefined,
      incidentResponseOwner: e.incidentResponseOwner?.trim() || undefined,
      knownIncidents: Boolean(e.knownIncidents),
      escalationPathDefined: Boolean(e.escalationPathDefined),
      approvals: (e.approvals ?? [])
        .filter((a) => a && (a.approverName || a.signatureConfirmation || a.approverRole))
        .map((a) => ({
          ...(a.id != null ? { id: a.id } : {}),
          ...(e.id != null ? { incidentId: e.id } : {}),
          approverName: a.approverName?.trim() || undefined,
          approverRole: a.approverRole?.trim() || undefined,
          approverCommittee: a.approverCommittee?.trim() || undefined,
          approvalDate: a.approvalDate || undefined,
          approvalStatus: a.approvalStatus || undefined,
          approvalComments: a.approvalComments?.trim() || undefined,
          approvalLevel: a.approvalLevel || undefined,
          signatureConfirmation: a.signatureConfirmation?.trim() || undefined,
          jobPosition: a.jobPosition?.trim() || undefined,
        })),
    }));
}

export function buildChangeManagements(wizardData) {
  const entries = wizardData.changeManagement?.entries ?? [];
  return entries
    .filter((e) => (e.title || '').trim())
    .map((e) => ({
      ...(e.id != null ? { id: e.id } : {}),
      title: e.title.trim(),
      description: e.description?.trim() || undefined,
      changeType: e.changeType || undefined,
      priority: e.priority || undefined,
      riskLevel: e.riskLevel || undefined,
      reasonForChange: e.reasonForChange?.trim() || undefined,
      requestedBy: e.requestedBy?.trim() || undefined,
      requestedDate: toApiDateTime(e.requestedDate),
      currentVersion: e.currentVersion?.trim() || undefined,
      targetVersion: e.targetVersion?.trim() || undefined,
      implementationPlan: e.implementationPlan?.trim() || undefined,
      rollbackPlan: e.rollbackPlan?.trim() || undefined,
      testPlan: e.testPlan?.trim() || undefined,
      testResult: e.testResult?.trim() || undefined,
      scheduledStart: toApiDateTime(e.scheduledStart),
      scheduledEnd: toApiDateTime(e.scheduledEnd),
      actualStart: toApiDateTime(e.actualStart),
      actualEnd: toApiDateTime(e.actualEnd),
      downtimeRequired: Boolean(e.downtimeRequired),
      downtimeDuration:
        e.downtimeDuration === '' || e.downtimeDuration == null
          ? undefined
          : Number(e.downtimeDuration),
      implementationStatus: e.implementationStatus || undefined,
      implementedBy: e.implementedBy?.trim() || undefined,
      approvals: (e.approvals ?? [])
        .filter((a) => a?.approverName || a?.approverRole || a?.approvalStatus)
        .map((a) => ({
          ...(a.id != null ? { id: a.id } : {}),
          ...(e.id != null ? { changeManagementId: e.id } : {}),
          approverName: a.approverName || undefined,
          approverRole: a.approverRole || undefined,
          approverCommittee: a.approverCommittee || undefined,
          approvalDate: a.approvalDate || undefined,
          approvalStatus: a.approvalStatus || undefined,
          approvalComments: a.approvalComments?.trim() || undefined,
          approvalLevel: a.approvalLevel || undefined,
          signatureConfirmation: a.signatureConfirmation || undefined,
          jobPosition: a.jobPosition || undefined,
        })),
    }));
}

export function buildIntegrations(wizardData) {
  const entries = wizardData.integration?.entries ?? [];
  return entries
    .filter((e) => (e.sourceSystem || '').trim() && (e.targetSystem || '').trim())
    .map((e) => ({
      ...(e.id != null ? { id: e.id } : {}),
      sourceSystem: e.sourceSystem.trim(),
      targetSystem: e.targetSystem.trim(),
      integrationType: e.integrationType || undefined,
      integrationPurpose: e.integrationPurpose?.trim() || undefined,
      dataFlowDirection: e.dataFlowDirection || undefined,
      dataExchanged: e.dataExchanged?.trim() || undefined,
      integrationMethod: e.integrationMethod?.trim() || undefined,
      authenticationMethod: e.authenticationMethod?.trim() || undefined,
      integrationOwner: e.integrationOwner?.trim() || undefined,
      securityMeasures: e.securityMeasures?.trim() || undefined,
      integrationStatus: e.integrationStatus || undefined,
    }));
}

function buildApprovals(wizardData) {
  // Prefer deployment-stage approvals (1st/2nd Approval buttons) so sr_approval stays in sync.
  const identification = wizardData.softwareIdentification ?? {};
  const approvalsByStage = identification.deploymentStatusApprovals ?? {};
  const fromStages = [];

  DEPLOYMENT_STATUS.forEach((status) => {
    const key = dateKeyForStatus(status);
    (approvalsByStage[key] ?? []).forEach((row, index) => {
      const name = (row.name || '').trim();
      const email = (row.email || '').trim();
      if ((!name && !email) || !row.approvedAt) return;
      const approvedAt = String(row.approvedAt).trim();
      const approvalDate = approvedAt.includes('T')
        ? approvedAt.slice(0, 10)
        : approvedAt.slice(0, 10);
      fromStages.push({
        ...(row.systemApprovalId != null ? { id: row.systemApprovalId } : {}),
        approverName: name || email,
        approverRole: index === 0 ? '1st Approval' : '2nd Approval',
        approvalDate: approvalDate || undefined,
        approvalStatus: 'APPROVED',
        approvalComments:
          `Approved deployment status: ${status}` +
          (email ? ` | Approver email: ${email}` : ''),
        approvalLevel: index === 0 ? 'TECHNICAL' : 'BUSINESS',
        signatureConfirmation: email || undefined,
      });
    });
  });

  if (fromStages.length) return fromStages;

  // Legacy fallback (old Approval & Sign-off form fields).
  const approval = wizardData.approvalSignOff;
  if (!hasContent(approval)) return undefined;
  return [
    {
      ...(wizardData.meta?.approvalId != null ? { id: wizardData.meta.approvalId } : {}),
      ...(wizardData.meta?.reviewId != null ? { reviewId: wizardData.meta.reviewId } : {}),
      approverName: approval.reviewedBy?.trim() || undefined,
      approverCommittee: approval.reviewerRoleCommittee?.trim() || undefined,
      approvalDate: approval.approvalDate || undefined,
      approvalStatus: approval.approvalStatus || undefined,
    },
  ];
}

function hasDataModelInfo(dataModel) {
  if (!dataModel) return false;
  return (
    dataModel.personalData === true
    || (dataModel.sensitiveDataCategories?.length ?? 0) > 0
    || Boolean(dataModel.modelType)
    || Boolean(dataModel.trainingDataOrigin)
  );
}

function buildDataInformation(dataModel, meta) {
  const hasSources = Boolean((dataModel?.dataSourcesUsed ?? '').trim());
  if (!hasDataModelInfo(dataModel) && !hasSources) return null;
  const payload = {
    personalData: Boolean(dataModel.personalData),
    trainingDataOrigin: dataModel.trainingDataOrigin || undefined,
    modelType: dataModel.modelType || undefined,
    sensitiveDataCategories: dataModel.sensitiveDataCategories ?? [],
  };
  if (meta?.dataInformationId != null) {
    payload.id = meta.dataInformationId;
  }
  return payload;
}

function buildSoftwareRisks(wizardData) {
  const entries = wizardData.riskImpact?.risks ?? [];
  return entries
    .filter((e) => (e.riskName || '').trim() || (e.riskDescription || '').trim() || (e.riskCategory || '').trim())
    .map((e) => ({
      ...(e.id != null ? { id: e.id } : {}),
      ...(e.riskRegisterId != null ? { riskRegisterId: e.riskRegisterId } : {}),
      riskCategory: e.riskCategory?.trim() || undefined,
      resourcesImpacted: e.resourcesImpacted?.trim() || undefined,
      riskName: e.riskName?.trim() || undefined,
      riskPriority: e.riskPriority || undefined,
      riskDescription: e.riskDescription?.trim() || undefined,
      riskOwner: e.riskOwner?.trim() || undefined,
      impact: e.impact?.trim() || undefined,
      causes: e.causes?.trim() || undefined,
      controlsAndMitigation: e.controlsAndMitigation?.trim() || undefined,
      status: e.status || undefined,
    }));
}

function buildRiskAssessment(riskImpact, meta) {
  if (!hasContent(riskImpact)) return null;
  return {
    usesArtificialIntelligence: Boolean(riskImpact.usesArtificialIntelligence),
    knownBias: Boolean(riskImpact.knownBias),
    biasDescription: riskImpact.biasDescription?.trim() || undefined,
  };
}

function buildBusinessCase(businessCase, meta) {
  if (!businessCase) return null;
  return {
    ...(businessCase.id != null
      ? { id: businessCase.id }
      : meta?.businessCaseId != null
        ? { id: meta.businessCaseId }
        : {}),
    caseDate: businessCase.caseDate || undefined,
    submittedBy: businessCase.submittedBy?.trim() || undefined,
    titleRole: businessCase.titleRole?.trim() || undefined,
    projectOverview: businessCase.projectOverview?.trim() || undefined,
    backgroundCurrentSituation: businessCase.backgroundCurrentSituation?.trim() || undefined,
    proposedSolution: businessCase.proposedSolution?.trim() || undefined,
    technicalRequirementsIntegration:
      businessCase.technicalRequirementsIntegration?.trim() || undefined,
    securityComplianceConsiderations:
      businessCase.securityComplianceConsiderations?.trim() || undefined,
    implementationPlan: businessCase.implementationPlan?.trim() || undefined,
    costEstimate: businessCase.costEstimate?.trim() || undefined,
    benefitsImpactsOnUsers: businessCase.benefitsImpactsOnUsers?.trim() || undefined,
    performanceMetrics: businessCase.performanceMetrics?.trim() || undefined,
    risksAndLimitations: businessCase.risksAndLimitations?.trim() || undefined,
  };
}

export function buildWizardPayload(wizardData) {
  const id = wizardData.softwareIdentification ?? {};
  const useCase = wizardData.useCase ?? {};
  const processRows = (useCase.businessProcesses ?? []).filter((row) => row.businessProcessId);
  const usesAi = Boolean(wizardData.riskImpact?.usesArtificialIntelligence);
  const approvalsPayload = buildApprovals(wizardData);

  return {
    softwareName: id.softwareName?.trim(),
    // Internal ID is assigned by the backend (S-001…).
    externalOrganisationReference: id.externalOrganisationReference?.trim() || undefined,
    contractStartDate: id.contractStartDate || undefined,
    contractEndDate: id.contractEndDate || undefined,
    contractDuration:
      id.contractDuration === '' || id.contractDuration == null
        ? undefined
        : Number(id.contractDuration),
    contractDurationUnit: id.contractDurationUnit || undefined,
    licenseStartDate: id.licenseStartDate || undefined,
    licenseEndDate: id.licenseEndDate || undefined,
    licenseDuration:
      id.licenseDuration === '' || id.licenseDuration == null
        ? undefined
        : Number(id.licenseDuration),
    licenseDurationUnit: id.licenseDurationUnit || undefined,
    deploymentEnvironment: id.deploymentEnvironment || undefined,
    systemIpAddress: id.systemIpAddress?.trim() || undefined,
    systemUrl: id.systemUrl?.trim() || undefined,
    hostingServerName: id.hostingServerName?.trim() || undefined,
    deploymentStatus: id.deploymentStatus || undefined,
    firstDeployedDate: id.deploymentStatusDates?.production || id.firstDeployedDate || undefined,
    deploymentStatusDates: {
      planned: id.deploymentStatusDates?.planned || undefined,
      development: id.deploymentStatusDates?.development || undefined,
      testing: id.deploymentStatusDates?.testing || undefined,
      pilot: id.deploymentStatusDates?.pilot || undefined,
      production: id.deploymentStatusDates?.production || undefined,
      retired: id.deploymentStatusDates?.retired || undefined,
      suspended: id.deploymentStatusDates?.suspended || undefined,
    },
    deploymentStatuses: buildDeploymentStatuses(id),
    businessProcesses: processRows.map((row) => ({
      businessProcessId: Number(row.businessProcessId),
      decisionType: row.decisionType || undefined,
      humanOversight: Boolean(row.humanOversight),
      humanOversightExplanation: row.humanOversight
        ? row.humanOversightExplanation?.trim() || undefined
        : undefined,
      businessOwner: row.businessOwner?.trim() || undefined,
    })),
    businessUnitIds: (id.businessUnitIds ?? []).map(Number),
    systemOwnerIds: (id.systemOwnerIds ?? []).map(Number),
    vendorIds: [...new Set((id.vendorIds ?? []).map(Number).filter(Boolean))],
    regulationIds: (
      wizardData.riskImpact?.regulationIds
      ?? wizardData.complianceGovernance?.regulationIds
      ?? []
    ).map(Number),
    dataInformation: usesAi
      ? (buildDataInformation(wizardData.dataModel, wizardData.meta)
        ?? {
          personalData: false,
          sensitiveDataCategories: [],
          ...(wizardData.meta?.dataInformationId != null
            ? { id: wizardData.meta.dataInformationId }
            : {}),
        })
      : {
          personalData: false,
          sensitiveDataCategories: [],
          ...(wizardData.meta?.dataInformationId != null
            ? { id: wizardData.meta.dataInformationId }
            : {}),
        },
    riskAssessment: buildRiskAssessment(wizardData.riskImpact, wizardData.meta),
    ...(useCase.businessCase
      ? { businessCase: buildBusinessCase(useCase.businessCase, wizardData.meta) }
      : wizardData.meta?.businessCaseId != null
        ? { removeBusinessCase: true }
        : {}),
    dataSources: usesAi ? buildDataSources(wizardData.dataModel, wizardData.meta) : [],
    documents: buildDocuments(wizardData.complianceGovernance, wizardData.meta),
    reviews: buildReview(wizardData),
    monitoringReviews: buildMonitoringReviews(wizardData),
    incidents: buildIncidents(wizardData),
    softwareRisks: buildSoftwareRisks(wizardData),
    changeManagements: buildChangeManagements(wizardData),
    integratedWithOtherSystems:
      wizardData.integration?.integratedWithOtherSystems == null
        ? undefined
        : Boolean(wizardData.integration.integratedWithOtherSystems),
    integrations: buildIntegrations(wizardData),
    ...(approvalsPayload != null ? { approvals: approvalsPayload } : {}),
  };
}

/** Step 1 — persist identification only (do not wipe nested collections). */
export function buildCoreStepPayload(wizardData) {
  const id = wizardData.softwareIdentification ?? {};
  return {
    softwareName: id.softwareName?.trim(),
    externalOrganisationReference: id.externalOrganisationReference?.trim() || undefined,
    contractStartDate: id.contractStartDate || undefined,
    contractEndDate: id.contractEndDate || undefined,
    contractDuration:
      id.contractDuration === '' || id.contractDuration == null
        ? undefined
        : Number(id.contractDuration),
    contractDurationUnit: id.contractDurationUnit || undefined,
    licenseStartDate: id.licenseStartDate || undefined,
    licenseEndDate: id.licenseEndDate || undefined,
    licenseDuration:
      id.licenseDuration === '' || id.licenseDuration == null
        ? undefined
        : Number(id.licenseDuration),
    licenseDurationUnit: id.licenseDurationUnit || undefined,
    deploymentEnvironment: id.deploymentEnvironment || undefined,
    systemIpAddress: id.systemIpAddress?.trim() || undefined,
    systemUrl: id.systemUrl?.trim() || undefined,
    hostingServerName: id.hostingServerName?.trim() || undefined,
    deploymentStatus: id.deploymentStatus || undefined,
    firstDeployedDate: id.deploymentStatusDates?.production || id.firstDeployedDate || undefined,
    deploymentStatusDates: {
      planned: id.deploymentStatusDates?.planned || undefined,
      development: id.deploymentStatusDates?.development || undefined,
      testing: id.deploymentStatusDates?.testing || undefined,
      pilot: id.deploymentStatusDates?.pilot || undefined,
      production: id.deploymentStatusDates?.production || undefined,
      retired: id.deploymentStatusDates?.retired || undefined,
      suspended: id.deploymentStatusDates?.suspended || undefined,
    },
    deploymentStatuses: buildDeploymentStatuses(id),
    businessUnitIds: (id.businessUnitIds ?? []).map(Number),
    systemOwnerIds: (id.systemOwnerIds ?? []).map(Number),
    vendorIds: [...new Set((id.vendorIds ?? []).map(Number).filter(Boolean))],
  };
}
